import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsitePartUsed } from '../../../api/src/types/onsite-types.js';

// ─── Types for scan & sell ──────────────────────────────────

export interface ScannedItem {
  type: string;
  item: {
    id: string;
    name: string;
    sku?: string;
    barcode?: string;
    description?: string;
    sell_price?: number;
    cost_price?: number;
    serial_number?: string;
    category?: string;
    [key: string]: unknown;
  };
}

export interface SellPayload {
  job_id: string;
  product_id: string;
  product_name?: string;
  sku?: string;
  serial_number?: string;
  quantity: number;
  unit_price: number;
  billable: boolean;
}

export interface SellResponse {
  part: OnsitePartUsed;
  product_id: string;
  serial_number: string | null;
  message: string;
}

// ─── List parts for a job ───────────────────────────────────

export function useJobParts(jobId: string | undefined) {
  return useQuery({
    queryKey: ['parts', jobId],
    queryFn: () =>
      onsiteApiFetch<{ parts: OnsitePartUsed[] }>(
        `/api/v1/onsite/parts?job_id=${jobId}`
      ),
    enabled: !!jobId,
  });
}

// ─── Create a manual part ───────────────────────────────────

export function usePartsCreate(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (part: Omit<OnsitePartUsed, 'id' | 'type' | 'org_id' | 'tech_id' | 'added_at'>) =>
      onsiteApiFetch<{ part: OnsitePartUsed }>('/api/v1/onsite/parts', {
        method: 'POST',
        body: JSON.stringify(part),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts', jobId] });
    },
  });
}

// ─── Update a part ──────────────────────────────────────────

export function usePartsUpdate(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partId, ...patch }: { partId: string } & Partial<OnsitePartUsed>) =>
      onsiteApiFetch<{ part: OnsitePartUsed }>(`/api/v1/onsite/parts/${partId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts', jobId] });
    },
  });
}

// ─── Delete a part ──────────────────────────────────────────

export function usePartsDelete(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partId: string) =>
      onsiteApiFetch(`/api/v1/onsite/parts/${partId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts', jobId] });
    },
  });
}

// ─── Scan item via QR code ──────────────────────────────────

export function useScanItem() {
  return useMutation({
    mutationFn: (qrCode: string) =>
      onsiteApiFetch<ScannedItem>(
        `/api/v1/onsite/parts/scan/${encodeURIComponent(qrCode)}`
      ),
  });
}

// ─── Sell scanned item to job ───────────────────────────────

export function useSellItem(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SellPayload) =>
      onsiteApiFetch<SellResponse>('/api/v1/onsite/parts/sell', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts', jobId] });
    },
  });
}
